import React, { PureComponent } from 'react';
import {
  Theme,
  WithStyles,
  createStyles,
  withStyles,
} from '@lifeomic/chroma-react/styles';
import { SurveyItemComponent } from './SurveyItem';
import {
  withSurveyContext,
  withSurveyResponseContext,
  withIsReadOnly,
  WithSurveyContextInjectedProps,
  WithSurveyResponseContextInjectedProps,
  WithIsReadOnlyInjectedProps,
} from './Contexts';
import { Formik } from 'formik';
import SubmitButton, { DialogText } from './SubmitButton';
import SurveyLayout from './SurveyLayout';
import { SurveyOverrideClasses } from './SurveyOverrideClasses';
import clsx from 'clsx';
import ErrorScrollWrapper from './ErrorScrollWrapper';
import SaveProgressButton from './SaveProgressButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import withResponseSubject, {
  WithResponseSubjectInjectedProps,
} from './hoc/withResponseSubject';
import { injectIntl, IntlShape } from 'react-intl';
import getFormMeta from '../util/getFormMeta';

interface SurveyFormOwnProps {
  dialogText: DialogText;
  onCompletion: () => void;
  onSaveProgress: () => void;
  overrideClasses: SurveyOverrideClasses;
  hideSaveProgressButton?: boolean;
  intl: IntlShape;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      height: '100%',
    },
    paginationControlContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: theme.spacing(3),
    },
    itemsContainer: {
      width: '100%',
    },
    paper: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(2),
    },
    controlContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(3),
    },
    form: {
      width: '100%',
      height: '100%',
    },
  });

type SurveyFormProps = SurveyFormOwnProps &
  WithStyles<typeof styles> &
  WithSurveyContextInjectedProps &
  WithIsReadOnlyInjectedProps &
  WithSurveyResponseContextInjectedProps &
  WithResponseSubjectInjectedProps;

class SurveyForm extends PureComponent<SurveyFormProps> {
  private lastSubmittedBy: 'submit' | 'save';
  private submittedOn: string;
  private completedBy: string;
  private isComplete: boolean;

  surveyInitialValues: Record<string, any>;

  constructor(props: SurveyFormProps) {
    super(props);
    // initial values should not change during the lifecycle of this sub-form
    this.surveyInitialValues =
      props.survey?.getInitialValues(props.surveyResponse) ?? {};

    const { submittedOnMessage, completedByMessage } = getFormMeta(
      props.intl,
      props.surveyResponse?.authoredDate,
      props.patient,
    );

    this.submittedOn = submittedOnMessage;
    this.completedBy = completedByMessage;
    this.isComplete = props.surveyResponse.isComplete;
  }

  handleSubmit = (_values: any) => {
    if (this.lastSubmittedBy === 'save') {
      this.props.onSaveProgress();
    }
    this.props.onCompletion();
  };

  render() {
    const {
      classes,
      survey,
      isReadOnly,
      dialogText,
      overrideClasses,
      hideSaveProgressButton,
    } = this.props;

    return (
      <div className={clsx(classes.root, overrideClasses.formRoot)}>
        <Formik
          initialValues={this.surveyInitialValues}
          onSubmit={this.handleSubmit}
          render={({ handleSubmit, submitForm, errors, isSubmitting }) => (
            <form
              onSubmit={handleSubmit}
              className={clsx(classes.form, overrideClasses.form)}
            >
              <ErrorScrollWrapper errors={errors} isSubmitting={isSubmitting}>
                <SurveyLayout
                  isPaginated={false}
                  surveyContent={
                    <>
                      {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
                      {survey.surveyItems.map(surveyItem => (
                        <SurveyItemComponent
                          key={surveyItem.link}
                          surveyItem={surveyItem}
                        />
                      ))}
                    </>
                  }
                  rightButton={
                    <div>
                      {this.isComplete && (
                        <>
                          <Text>{this.submittedOn}</Text>
                          <Text>{this.completedBy}</Text>
                        </>
                      )}
                      {isReadOnly || hideSaveProgressButton ? null : (
                        <div className={classes.controlContainer}>
                          <SaveProgressButton
                            overrideClasses={overrideClasses}
                            dialogText={dialogText}
                            submitIncompleteForm={() => {
                              this.lastSubmittedBy = 'save';
                              submitForm();
                            }}
                          />
                          <SubmitButton
                            overrideClasses={overrideClasses}
                            dialogText={dialogText}
                            submitForm={() => {
                              this.lastSubmittedBy = 'submit';
                              submitForm();
                            }}
                          />
                        </div>
                      )}
                    </div>
                  }
                />
              </ErrorScrollWrapper>
            </form>
          )}
        />
      </div>
    );
  }
}

export default withStyles(styles)(
  withSurveyContext(
    withSurveyResponseContext(
      withIsReadOnly(withResponseSubject(injectIntl(SurveyForm))),
    ),
  ),
);
